/**
 * Helpers for deciding which rooms are bookable / interest-worthy in the
 * lead + booking UIs.
 *
 * A room qualifies when:
 *   - it has at least one free bed AND it's in a non-blocking status, OR
 *   - it's on `notice` and the latest planned check-out date is today/past
 *     (i.e. the outgoing tenant should already be out, so the room is about
 *      to free up — owners want to start showing it to new leads).
 *
 * Statuses excluded outright:
 *   - `fully_occupied` (no beds)
 *   - `booked`         (already locked by another booking)
 *   - `under_maintenance`
 *   - `notice` where planned_check_out_date is still in the future
 */

const toDate = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Latest planned check-out among notice-period tenants in the room.
 * Returns a Date or null if none.
 */
const latestPlannedExit = (room) => {
  const tenants = (room?.tenants || []).filter(t => t.status === 'notice');
  const dates = tenants
    .map(t => toDate(t.planned_check_out_date || t.check_out_date))
    .filter(Boolean);
  if (dates.length === 0) return null;
  return new Date(Math.max(...dates.map(d => d.getTime())));
};

/**
 * Does the room have any free bed right now?
 */
export const hasFreeBed = (room) => {
  const total = Number(room?.total_beds || 0);
  const occupied = Number(room?.occupied_beds || 0);
  return total > occupied;
};

/**
 * Is this room a candidate for a NEW booking right now?
 */
export const isBookableNow = (room) => {
  if (!room) return false;
  const status = room.status;

  // Hard blocks — no point showing
  if (status === 'under_maintenance') return false;
  if (status === 'booked') return false;

  // Notice: include only when last planned exit is in the past (or today)
  if (status === 'notice') {
    const exit = latestPlannedExit(room);
    if (!exit) return false;          // missing date → safer to hide
    return exit <= startOfToday();
  }

  // Fully occupied with no notice tenant → no beds
  if (status === 'fully_occupied') return false;

  // available / partially_occupied → bookable as long as a bed is free
  return hasFreeBed(room);
};

/**
 * Filter a list of rooms to only those bookable now.
 */
export const filterBookableRooms = (rooms) => (rooms || []).filter(isBookableNow);

/**
 * Friendly label tag for the option text — tells the owner *why* this
 * room is in the bookable list (e.g. "notice exit was yesterday").
 */
export const bookableTag = (room) => {
  if (!room) return '';
  if (room.status === 'notice') {
    const exit = latestPlannedExit(room);
    if (!exit) return '';
    const today = startOfToday();
    const diffDays = Math.floor((today - exit) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'notice exit today';
    if (diffDays === 1) return 'notice exit yesterday';
    return `notice exit ${diffDays}d ago`;
  }
  if (room.status === 'under_maintenance') {
    const until = toDate(room.maintenance_until);
    if (!until) return 'under maintenance';
    const today = startOfToday();
    const diffDays = Math.ceil((until - today) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return 'maintenance ended';
    if (diffDays === 1) return 'maintenance ends tomorrow';
    return `maintenance ends in ${diffDays}d`;
  }
  if (room.status === 'booked') return 'currently booked';
  if (room.status === 'fully_occupied') return 'fully occupied';
  const free = Number(room.total_beds || 0) - Number(room.occupied_beds || 0);
  return `${free} bed${free === 1 ? '' : 's'} free`;
};
